import React, { useState, useEffect, useRef, createRef, memo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useAnimation, AnimatePresence } from 'framer-motion'
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil'
import { 
  selectedClients as selectedClientsState, 
  selectedClient as selectedClientState,
  currentFix as currentFixState,
  lastSection as lastSectionState,
} from '../../store/uiStore'
import { 
  clientsData as clientsDataState,
} from '../../store/dataStore'
import CustomScroller from 'react-custom-scroller'
import useWindowResize from '../../hooks/useWindowResize'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'

import {
  ClientsSection,
  ClientsWrapper,
  ClientsNav,
  ClientsSlider,
  ClientsSlide,
  ClientItems,
  Item,
  SlideDescription,
} from '../../styles/clientsStyles.js'

const Slide = memo(
  React.forwardRef(({ isVisible, item, type }, ref) => {

    const windowSize = useWindowResize()

    const setSelectedClient = useSetRecoilState(selectedClientState)

    const sliderOptions = {
      dots: true,
      arrows: false,
      speed: 500,
      slidesToShow: type === 'clientes' ? 6 : 4,
      slidesToScroll: type === 'clientes' ? 6 : 4,
      infinite: false,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: type === 'clientes' ? 4 : 3,
            slidesToScroll: type === 'clientes' ? 4 : 3,
          },
        },
        {
          breakpoint: 860,
          settings: {
            slidesToShow: type === 'clientes' ? 3 : 2,
            slidesToScroll: type === 'clientes' ? 3 : 2,
          },
        },
        {
          breakpoint: 420,
          settings: {
            slidesToShow: type === 'clientes' ? 2 : 1,
            slidesToScroll: type === 'clientes' ? 2 : 1,
          },
        },
      ],
    }

    const slideVariants = {
      hidden: {
        opacity: 0,
        x: '-20%',
        visibility: 'hidden',
        zIndex: -1,
      },
      visible: {
        opacity: 1,
        x: 0,
        visibility: 'visible',
        zIndex: 1,
      },
      exit: {
        opacity: 0,
        x: '20%',
        visibility: 'visible',
        zIndex: 1,
      },
    }
    const itemsVariants = {
      hidden: {
        opacity: 0,
        transition: {
          ease: 'linear',
          delayChildren: 0,
        },
      },
      visible: {
        opacity: 1,
        transition: {
          ease: 'linear',
          staggerChildren: 0.25,
          delayChildren: 0.75,
        },
      },
    }
    const childVariants = {
      hidden: {
        y: '50%',
        opacity: 0,
      },
      visible: {
        y: 0,
        opacity: 1,
      },
    }
    const playChildren = useAnimation(itemsVariants)

    const handleClientClick = useCallback((client) => {
      if (client.description) {
        setSelectedClient((last) => client.id)
      } else {
        window.open(client.url, "_blank") //to open new page
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if (isVisible) {
        playChildren.start('visible')
      }
    }, [isVisible, playChildren])
    return (
      <AnimatePresence exitBeforeEnter>
        {isVisible && (
          <ClientsSlide
            ref={ref}
            initial='hidden'
            animate={isVisible ? 'visible' : 'hidden'}
            exit='exit'
            variants={slideVariants}
            transition={{
              default: {
                duration: 0.25,
              },
              opacity: {
                duration: 0.5,
                ease: 'easeOut',
              },
              x: {
                duration: 1.25,
                ease: 'easeOut',
              },
            }}>
            <SlideDescription
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
            <ClientItems
              initial='hidden'
              animate={playChildren}
              variants={itemsVariants}
              className={type}
              >
              <Slider {...sliderOptions}>
                {item.items.map((item) => (
                  <Item
                    key={item.name}
                    variants={childVariants}
                    transition={{
                      duration: 1.25,
                      ease: 'easeOut',
                    }}>
                    <button
                      key={`button-client-${item.id}`}
                      type='button'
                      onClick={() => handleClientClick(item)}>
                      <img src={item.logo} alt={item.name} />
                      { windowSize.width > 420 && (
                        <h3>{item.name}</h3>
                      )}
                    </button>
                  </Item>
                ))}
              </Slider>
            </ClientItems>
          </ClientsSlide>
        )}
      </AnimatePresence>
    )
  })
)

Slide.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
}

const Clients = ({ visible }) => {
  const clientsData = useRecoilValue(clientsDataState)
  const slides = Object.values(clientsData).map((item) => {
    return item.title
  })

  const [selectedClients, setSelectedClients] = useRecoilState(
    selectedClientsState
  )
  const setLastSection = useSetRecoilState(
    lastSectionState
  )

  const [currentFix, setCurrentFix] = useRecoilState(
    currentFixState
  )
  const [sliderHeight, setSliderHeight] = useState(0)

  const clientsSection = useRef(null)
  const clientsNav = useRef(null)
  const clientsSlider = useRef(null)
  const slidesRef = useRef(slides.map(() => createRef()))
  const clientScroller = useRef(null)

  const clientsSectionVars = {
    hidden: {
      visibility: 'hidden',
      zIndex: -1,
      opacity: 0,
    },
    visible: {
      visibility: 'visible',
      zIndex: 1,
      opacity: 1,
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.75,
        delay: 0,
      },
      transitionEnd: {
        visibility: 'hidden',
        zIndex: -1,
      },
    },
  }

  const clientsNavVars = {
    hidden: {
      y: '-250%',
      opacity: 0,
      transition: {
        ease: 'easeOut',
        delay: 0,
        duration: 3.5,
      },
    },
    visible: {
      y: 0,
      opacity: 1,
    },
  }

  const clientsSliderVars = {
    hidden: {
      y: '80%',
      opacity: 0,
      transition: {
        ease: 'easeOut',
        delay: 0,
        duration: 3.5,
      },
    },
    visible: {
      y: 0,
      opacity: 1,
    },
  }

  const clientsSectionAnims = useAnimation(clientsSectionVars)
  const clientsNavAnims = useAnimation(clientsNavVars)
  const clientsSliderAnims = useAnimation(clientsSliderVars)

  const handleClientsNav = (slide) => {
    setLastSection((last) => currentFix)
    setSelectedClients((last) => slide)
    setCurrentFix((last) => slide === 0 ? 'clientes' : 'aliados')
  }

  useEffect(() => {
    const height = slidesRef.current[
      selectedClients
    ].current.getBoundingClientRect().height
    setSliderHeight(height)
  }, [selectedClients])

  useEffect(() => {
    if (visible) {
      clientsSectionAnims.start('visible')
      clientsNavAnims.start('visible')
      clientsSliderAnims.start('visible')
      clientScroller.current.scrollTop = 0
    } else {
      clientsNavAnims.start('hidden')
      clientsSliderAnims.start('hidden')
      clientsSectionAnims.start('exit')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  return (
    <ClientsSection
      ref={clientsSection}
      initial='hidden'
      animate={clientsSectionAnims}
      variants={clientsSectionVars}
      transition={{
        opacity: {
          duration: 1,
          ease: 'linear',
        },
        default: {
          duration: 0,
          ease: 'none',
        },
      }}>
      <CustomScroller ref={clientScroller} className='Scroller' innerClassName='ScrollerContent'>
        <ClientsWrapper>
          <ClientsNav
            ref={clientsNav}
            initial='hidden'
            animate={clientsNavAnims}
            variants={clientsNavVars}
            transition={{
              duration: 1.25,
              delay: 0.75,
              ease: 'easeOut',
            }}>
            {slides.map((item, index) => (
              <button
                key={`button-${item}`}
                type='button'
                className={selectedClients === index ? 'active' : ''}
                onClick={() => handleClientsNav(index)}>
                {item}
              </button>
            ))}
          </ClientsNav>
          <ClientsSlider
            ref={clientsSlider}
            style={{
              height: sliderHeight,
            }}
            initial='hidden'
            animate={clientsSliderAnims}
            variants={clientsSliderVars}
            transition={{
              duration: 1.25,
              delay: 0.75,
              ease: 'easeOut',
            }}>
            {clientsData.map((item, index) => (
              <Slide
                ref={slidesRef.current[index]}
                key={`slide-${item.title.toLowerCase()}`}
                isVisible={index === selectedClients}
                type={item.title.toLowerCase()}
                item={item}
              />
            ))}
          </ClientsSlider>
        </ClientsWrapper>
      </CustomScroller>
    </ClientsSection>
  )
}

Clients.propTypes = {
  visible: PropTypes.bool.isRequired,
}

Slide.propTypes = {
  isVisible: PropTypes.bool.isRequired,
}

export default memo(Clients)
